import React, { useState } from 'react';
import { Alert, Button, DatePicker, Empty, Form, Input, message, Modal, Popconfirm, Progress, Select, Space, Switch, Tag, Typography, Upload } from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined, FilePdfOutlined, FileTextOutlined, SwapOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../services/api';
import { TrainingDocument, TrainingFolder } from '../types';
import { getApiErrorMessage } from '../utils/apiError';
import { dateMismatchMessage, formatTrainingDate, formatUploadDate, inferTrainingDate } from '../utils/trainingDates';
import { trainingUploadError, validateTrainingFile } from '../utils/trainingUpload';
import ReplaceTrainingFile from './ReplaceTrainingFile';
import AdminTranscriptModal from './AdminTranscriptModal';

const { Title, Text } = Typography;
type PendingDocument = { uid: string; file: File; title: string; trainingDate: string | null };

export const inferDocumentDate = inferTrainingDate;

const AdminTrainingDocuments: React.FC<{
  folders: TrainingFolder[];
  folder: TrainingFolder | null;
  onRefresh: () => Promise<void>;
  embedded?: boolean;
  onOpen?: (document: TrainingDocument) => void;
}> = ({ folders, folder, onRefresh, embedded = false, onOpen }) => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [targetFolder, setTargetFolder] = useState<number>();
  const [files, setFiles] = useState<PendingDocument[]>([]);
  const [busy, setBusy] = useState(false);
  const [percent, setPercent] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [editing, setEditing] = useState<TrainingDocument | null>(null);
  const [replacing, setReplacing] = useState<TrainingDocument | null>(null);
  const [editingText, setEditingText] = useState<TrainingDocument | null>(null);
  const [actionId, setActionId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const editedDate = Form.useWatch('training_date', form);
  const editedTitle = Form.useWatch('title', form);
  const sourceDateName = editing && (inferTrainingDate(editing.original_filename) ? editing.original_filename : editedTitle || editing.title);
  const dateWarning = sourceDateName ? dateMismatchMessage(editedDate?.format('YYYY-MM-DD'), sourceDateName) : null;

  const upload = async () => {
    if (busy) return;
    if (!targetFolder || !files.length || files.some(file => !file.title.trim())) {
      message.error('Выберите папку, файлы и укажите названия');
      return;
    }
    setBusy(true);
    setUploadError('');
    setPercent(0);
    let saved = 0;
    try {
      for (const item of files) {
        const completedBeforeThisFile = saved;
        const body = new FormData();
        body.append('folder_id', String(targetFolder));
        body.append('title', item.title.trim());
        if (item.trainingDate) body.append('training_date', item.trainingDate);
        body.append('file', item.file);
        await api.post('/video/admin/documents', body, {
          timeout: 0,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: event => {
            if (event.total) setPercent(Math.round((completedBeforeThisFile + event.loaded / event.total) / files.length * 100));
          },
        });
        saved += 1;
        setFiles(current => current.filter(file => file.uid !== item.uid));
      }
      message.success(`Загружено документов: ${saved}`);
      setUploadOpen(false);
    } catch (err) {
      setUploadError(trainingUploadError(err, 'Не удалось загрузить документ. Оставшиеся файлы можно отправить повторно.'));
    } finally {
      setBusy(false);
      if (saved) await onRefresh();
    }
  };

  const save = async () => {
    if (!editing || busy) return;
    setBusy(true);
    try {
      const values = await form.validateFields();
      await api.patch(`/video/admin/documents/${editing.id}`, {
        ...values,
        training_date: values.training_date?.format('YYYY-MM-DD') || null,
      });
      setEditing(null);
      message.success('Документ обновлён');
      await onRefresh();
    } catch (err: any) {
      if (!err?.errorFields) message.error(getApiErrorMessage(err, 'Не удалось сохранить документ'));
    } finally {
      setBusy(false);
    }
  };

  const changeVisibility = async (document: TrainingDocument, isActive: boolean) => {
    setActionId(document.id);
    try {
      await api.patch(`/video/admin/documents/${document.id}`, { is_active: isActive });
      await onRefresh();
    } catch (err) {
      message.error(getApiErrorMessage(err, 'Не удалось изменить доступность документа'));
    } finally { setActionId(null); }
  };

  const remove = async (document: TrainingDocument) => {
    setActionId(document.id);
    try {
      await api.delete(`/video/admin/documents/${document.id}`);
      message.success('Документ удалён');
      await onRefresh();
    } catch (err) {
      message.error(getApiErrorMessage(err, 'Не удалось удалить документ'));
    } finally { setActionId(null); }
  };

  return (
    <section className={`video-admin-section ${embedded ? 'video-admin-documents-nested' : ''}`}>
      <div className="video-admin-section-header">
        <div><Title level={embedded ? 3 : 2}>{embedded ? 'Документы' : 'Документы в папке'}</Title>{!embedded && <Text type="secondary">{folder?.title || 'Выберите папку обучения'}</Text>}</div>
        <Button data-guide="upload-documents" icon={<UploadOutlined />} disabled={embedded ? !folder?.is_active : !folders.some(item => item.is_active)} onClick={() => {
          setTargetFolder(folder?.is_active ? folder.id : folders.find(item => item.is_active)?.id);
          setFiles([]); setUploadError(''); setPercent(0); setUploadOpen(true);
        }}>Загрузить документы</Button>
      </div>
      {folder?.documents?.length ? (
        <ul className="video-admin-content-list" aria-label="Документы папки">
          {folder.documents.map(document => (
            <li key={document.id} className={`video-admin-content-row ${document.is_active && folder.is_active ? '' : 'hidden'}`}>
              <span className="video-admin-row-icon" aria-hidden="true">{document.document_kind === 'presentation' ? <FilePdfOutlined /> : <FileTextOutlined />}</span>
              <div className="video-admin-row-main">
                <div className="video-admin-row-heading">
                  <Title level={3}>{onOpen ? <button type="button" className="video-admin-material-open" aria-label={`Открыть документ «${document.title}»`} onClick={() => onOpen(document)}>{document.title}</button> : document.title}</Title>
                  <Tag color={document.is_active && folder.is_active ? 'success' : 'default'}>{!folder.is_active ? 'Папка в архиве' : document.is_active ? 'Доступно' : 'Скрыто'}</Tag>
                </div>
                <div className="video-admin-row-description"><Text type="secondary" className={!document.description ? 'video-admin-row-filename' : undefined}>{document.description || document.original_filename}</Text></div>
                <div className="video-admin-row-meta">
                  <Text type="secondary">{document.document_kind === 'presentation' ? 'Презентация · PDF' : 'Транскрибация · TXT'}</Text>
                  <Text type="secondary">Дата обучения: {formatTrainingDate(document.training_date)}</Text>
                  <Text type="secondary">{(document.size_bytes / 1024 / 1024).toFixed(1)} МБ</Text>
                  <Text type="secondary">Загружено: {formatUploadDate(document.uploaded_at)} МСК</Text>
                </div>
                {dateMismatchMessage(document.training_date, inferTrainingDate(document.original_filename) ? document.original_filename : document.title) && <Alert className="video-admin-row-warning" type="warning" showIcon message={dateMismatchMessage(document.training_date, inferTrainingDate(document.original_filename) ? document.original_filename : document.title)} />}
              </div>
              <Space className="video-admin-row-actions" wrap size={[8, 8]}>
                {onOpen && <Button icon={<EyeOutlined />} onClick={() => onOpen(document)}>Открыть</Button>}
                <div className="video-admin-visibility"><Switch aria-label={`Доступность документа «${document.title}»`} checked={document.is_active} loading={actionId === document.id} disabled={actionId !== null} onChange={value => changeVisibility(document, value)} /><Text>Доступ сотрудникам</Text></div>
                <Button icon={<EditOutlined />} onClick={() => {
                  form.setFieldsValue({ ...document, training_date: document.training_date ? dayjs(document.training_date) : null });
                  setEditing(document);
                }}>Изменить</Button>
                <Button data-guide="replace-file" icon={<SwapOutlined />} onClick={() => setReplacing(document)}>Заменить файл</Button>
                {document.document_kind === 'transcript' && <Button data-guide="edit-transcript" icon={<FileTextOutlined />} onClick={() => setEditingText(document)}>Редактировать текст</Button>}
                <Popconfirm title="Удалить документ и отметки о прочтении?" description="Для обновления файла с сохранением отметок используйте «Заменить файл»." okText="Удалить" cancelText="Отмена" onConfirm={() => remove(document)}>
                  <Button danger icon={<DeleteOutlined />} loading={actionId === document.id} disabled={actionId !== null}>Удалить</Button>
                </Popconfirm>
              </Space>
            </li>
          ))}
        </ul>
      ) : <Empty description="Презентации и транскрибации пока не добавлены" />}

      <Modal className="video-admin-modal" title="Загрузка документов" width={760} open={uploadOpen} onCancel={() => !busy && setUploadOpen(false)} onOk={upload} okText="Загрузить" confirmLoading={busy} closable={!busy} maskClosable={!busy}>
        <Space data-guide="document-upload-form" direction="vertical" size={16} style={{ width: '100%' }}>
          <Text>PDF — до 100 МБ, TXT — до 5 МБ. Видео, презентация и текст с одинаковой датой в одной папке открываются на одном экране. Дату обучения можно изменить; дата загрузки учитывается отдельно.</Text>
          <Select aria-label="Папка для документов" placeholder="Папка обучения" value={targetFolder} onChange={setTargetFolder} disabled={busy} style={{ width: '100%' }} options={folders.filter(item => item.is_active).map(item => ({ value: item.id, label: item.title }))} />
          <Upload multiple accept=".pdf,.txt" disabled={busy} showUploadList={false} beforeUpload={file => {
            if (!/\.(pdf|txt)$/i.test(file.name)) { message.error('Выберите PDF или TXT'); return Upload.LIST_IGNORE; }
            const validation = validateTrainingFile(file, /\.pdf$/i.test(file.name) ? 'presentation' : 'transcript');
            if (validation) { message.error(validation); return Upload.LIST_IGNORE; }
            setFiles(current => [...current, { uid: file.uid, file, title: file.name.replace(/\.(pdf|txt)$/i, '').trim().slice(0, 255), trainingDate: inferDocumentDate(file.name) }]);
            return false;
          }}><Button icon={<UploadOutlined />} disabled={busy}>Выбрать файлы</Button></Upload>
          {files.map(item => (
            <div key={item.uid} className="document-upload-row">
              <Text ellipsis>{item.file.name}</Text>
              <Input aria-label={`Название ${item.file.name}`} value={item.title} maxLength={255} disabled={busy} onChange={event => setFiles(current => current.map(file => file.uid === item.uid ? { ...file, title: event.target.value } : file))} />
              <DatePicker aria-label={`Дата ${item.file.name}`} value={item.trainingDate ? dayjs(item.trainingDate) : null} format="DD.MM.YYYY" disabled={busy} onChange={value => setFiles(current => current.map(file => file.uid === item.uid ? { ...file, trainingDate: value?.format('YYYY-MM-DD') || null } : file))} />
              <Button aria-label={`Убрать ${item.file.name}`} icon={<DeleteOutlined />} disabled={busy} onClick={() => setFiles(current => current.filter(file => file.uid !== item.uid))} />
            </div>
          ))}
          {uploadError && <Alert type="error" showIcon message={uploadError} />}
          {busy && <Progress percent={percent} status="active" />}
        </Space>
      </Modal>
      {replacing && <ReplaceTrainingFile key={replacing.id} material={replacing} kind={replacing.document_kind} onClose={() => setReplacing(null)} onReplaced={onRefresh} />}
      {editingText && <AdminTranscriptModal key={editingText.id} document={editingText} onClose={() => setEditingText(null)} onSaved={onRefresh} />}
      <Modal className="video-admin-modal" title="Редактировать документ" open={Boolean(editing)} onCancel={() => !busy && setEditing(null)} onOk={save} okText="Сохранить" confirmLoading={busy}>
        <Form form={form} layout="vertical" disabled={busy}>
          <Form.Item name="folder_id" label="Папка" rules={[{ required: true }]}><Select options={folders.map(item => ({ value: item.id, label: item.title }))} /></Form.Item>
          <Form.Item name="title" label="Название" rules={[{ required: true, whitespace: true }]}><Input maxLength={255} /></Form.Item>
          <Form.Item name="training_date" label="Дата обучения" extra="По этой дате объединяются презентация и текст в одной папке. Изменение даты только одного материала разделит их."><DatePicker format="DD.MM.YYYY" /></Form.Item>
          {dateWarning && <Alert type="warning" showIcon message={dateWarning} action={<Button onClick={() => form.setFieldValue('training_date', dayjs(inferTrainingDate(sourceDateName!)))}>Использовать дату из названия</Button>} />}
          <Form.Item name="description" label="Описание"><Input.TextArea maxLength={5000} rows={3} /></Form.Item>
          <Form.Item name="is_active" label="Доступен сотрудникам" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </section>
  );
};

export default AdminTrainingDocuments;
